/**
 * SpeyTech Accounting - Formatting Utilities
 * 
 * Consistent formatting for money, dates, and other values.
 */

/**
 * Format a number as GBP currency
 */
export function formatMoney(value: number | string, options?: {
  showSign?: boolean;
  currency?: string;
}): string {
  const { showSign = false, currency = 'GBP' } = options || {};
  
  const num = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(num)) {
    return '£0.00';
  }
  
  const formatted = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(num) / 100);
  
  if (showSign && num < 0) {
    return `(${formatted})`;
  }
  
  return formatted;
}

/**
 * Format a date for display
 */
export function formatDate(date: string | Date, options?: {
  format?: 'short' | 'medium' | 'long';
}): string {
  const { format = 'medium' } = options || {};
  
  const d = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(d.getTime())) {
    return '';
  }
  
  const formats: Record<string, Intl.DateTimeFormatOptions> = {
    short: { day: 'numeric', month: 'numeric', year: '2-digit' },
    medium: { day: 'numeric', month: 'short', year: 'numeric' },
    long: { day: 'numeric', month: 'long', year: 'numeric' },
  };
  
  return d.toLocaleDateString('en-GB', formats[format]);
}

/**
 * Format a date for input[type="date"]
 */
export function formatDateInput(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(d.getTime())) {
    return '';
  }
  
  return d.toISOString().split('T')[0];
}

/**
 * Format a date relative to now (e.g., "2 days ago")
 */
export function formatRelativeDate(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (days > 30) {
    return formatDate(d, { format: 'medium' });
  }
  if (days > 0) {
    return `${days} day${days === 1 ? '' : 's'} ago`;
  }
  if (hours > 0) {
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  }
  if (minutes > 0) {
    return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  }
  return 'Just now';
}

/**
 * Format a percentage
 */
export function formatPercent(value: number, decimals = 0): string {
  return `${value.toFixed(decimals)}%`;
}

/**
 * Parse a UK date string (DD/MM/YYYY) to ISO format
 */
export function parseUKDate(dateStr: string): string | null {
  const match = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return null;
  
  const [, day, month, year] = match;
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
}

/**
 * Get the status badge color for an invoice status
 */
export function getStatusColor(status: string): {
  bg: string;
  text: string;
  border: string;
} {
  const colors: Record<string, { bg: string; text: string; border: string }> = {
    draft: { bg: 'bg-surface-tertiary', text: 'text-content-secondary', border: 'border-edge-strong' },
    sent: { bg: 'bg-accent-tint', text: 'text-accent-strong', border: 'border-accent/20' },
    paid: { bg: 'bg-accent-tint', text: 'text-accent-strong', border: 'border-accent/20' },
    partial: { bg: 'bg-yellow-50 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-400', border: 'border-yellow-200 dark:border-yellow-800' },
    overdue: { bg: 'bg-red-50 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', border: 'border-red-200 dark:border-red-800' },
    cancelled: { bg: 'bg-surface-tertiary', text: 'text-content-tertiary', border: 'border-edge' },
    written_off: { bg: 'bg-surface-tertiary', text: 'text-content-tertiary', border: 'border-edge' },
    // Reserved terminal (ADR-013); renders nothing until Item 52 sets it.
    credited: { bg: 'bg-purple-50 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-400', border: 'border-purple-200 dark:border-purple-800' },
  };
  
  return colors[status] || colors.draft;
}
